import React, { useCallback, useEffect, useState } from "react";
import { useTeamQContext } from "../../../context/teamQContextProvider";
import { SelectInput } from "./filterInputs/selectInput";
import { slaStatusOptions, statusOptions } from "./constants";
import { ChooseFilters } from "./chooseFilters";
import { SavedFilters } from "./SavedFilters";
import { FilterActionButtons } from "./FilterActionButtons";
import { FilterName, FiltersAction } from "../../../context/teamQ/reducers/filtersReducer";
import { debounce } from "@material-ui/core";
import { InputChangeEvent } from "./filterInputs/types";
import { TextInput } from "./filterInputs/textInput";
import { BooleanInput } from "./filterInputs/booleanInput";
import { displaySettingsAction } from "../../../context/teamQ/reducers/displaySettingsReducer";

export default function TeamQSidenavFilters(): JSX.Element {
  const [canHide, setCanHide] = useState<boolean>(false);
  const [checkBoxChecked, setCheckBoxChecked] = useState<number | null>(null);

  const {
    state: { 
      filters,
      staticData: { classifications, clients, groups, teams, users } 
    },
    dispatch: { filters: filtersDispatch, displaySettings: displaySettingsDispatch },
    actions: { load },
  } = useTeamQContext();
  
  const debouncedLoad = useCallback(
    debounce(load, 500),
    []
  );

  useEffect(() => {
    if (checkBoxChecked !== null) {
      displaySettingsDispatch({
        type: displaySettingsAction.CHANGE_PAGE_NUMBER,
        pageIndex: "0",
      });
      debouncedLoad();
    }
  }, [checkBoxChecked]);
  
  const clearForm = (): void => {
    setCheckBoxChecked(null);
    filtersDispatch({ type: FiltersAction.CLEAR_VALUES });
    debouncedLoad();
  };

  const handleFilter = (event: InputChangeEvent, supressTextLoad?: boolean): void => {
    const name = event.target.name as FilterName;
    if (event.target.value === "" && ((name === FilterName.Group && filters[FilterName.Team].value === "") ||
      (name === FilterName.Team && filters[FilterName.Group].value === ""))) {
        // If both group and team are empty, clear the description filter 
      filtersDispatch({
        type: FiltersAction.SET_VALUE,
        filterName: FilterName.Description,
        value: "",
      });
    }
    filtersDispatch({
      type: FiltersAction.SET_VALUE,
      filterName: name,
      value: event.target.value as string,
    });
    setCheckBoxChecked(null);
    if (supressTextLoad) return;

    displaySettingsDispatch({
      type: displaySettingsAction.CHANGE_PAGE_NUMBER,
      pageIndex: "0",
    });

    if (supressTextLoad === undefined) {
      load();
    } else if (supressTextLoad === false) {
      debouncedLoad();
    }
  };

  const handleFilterBoolean = (name: string, value: boolean): void => {
    filtersDispatch({
      type: FiltersAction.SET_VALUE,
      filterName: name as FilterName,
      value: value ? "true" : "false"
    });
    setCheckBoxChecked(null);
    debouncedLoad();
  };

  const handleHide = (filterName: FilterName)=>{
    filtersDispatch({
      type: FiltersAction.HIDE,
      filterName,
    });
    debouncedLoad();
  }

  const disableDescriptionFilter = filters[FilterName.Group].value === "" && filters[FilterName.Team].value === "";

  return (
    <>
      <div className="title">
        Search Cases
        <button onClick={(): void => clearForm()} className="btn btn-danger btn-rounded float-right align-bottom">
          Clear
        </button>
      </div>
      <TextInput 
        canHide={false}
        filterName={FilterName.CaseReference} 
        handleFilter={handleFilter} 
        handleHide={handleHide}
        minLength={9}
        maxLength={9}
      />
      <TextInput
        canHide={canHide}
        filterName={FilterName.ClAccountId}
        handleFilter={handleFilter}
        handleHide={handleHide}
      />
      <SelectInput
        canHide={canHide}
        filterName={FilterName.Owner}
        options={users}
        handleFilter={handleFilter}
        handleHide={handleHide}
      />
      <SelectInput
        canHide={canHide}
        filterName={FilterName.Classification}
        options={classifications}
        handleFilter={handleFilter}
        handleHide={handleHide}
      />
      <SelectInput
        canHide={canHide}
        filterName={FilterName.Client}
        options={clients}
        handleFilter={handleFilter}
        handleHide={handleHide}
      />
      <TextInput 
        canHide={canHide}
        filterName={FilterName.AiProbability} 
        handleFilter={handleFilter} 
        handleHide={handleHide}
      />
      <TextInput
        canHide={canHide}
        disabled={disableDescriptionFilter}
        filterName={FilterName.Description} 
        handleFilter={handleFilter}
        handleHide={handleHide} 
        minLength={5}
      />
      <BooleanInput
        canHide={canHide}
        filterName={FilterName.StudioSendFailed}
        handleFilter={handleFilterBoolean}
        handleHide={handleHide}
      />
      <TextInput 
        canHide={canHide}
        filterName={FilterName.InvestorEmail} 
        handleFilter={handleFilter} 
        handleHide={handleHide}
      />
      <SelectInput
        canHide={canHide}
        filterName={FilterName.Group}
        options={groups}
        handleFilter={handleFilter}
        handleHide={handleHide}
      />
      <SelectInput
        canHide={canHide}
        filterName={FilterName.SlaStatus}
        options={slaStatusOptions}
        handleFilter={handleFilter}
        handleHide={handleHide}
      />
      <SelectInput
        canHide={canHide}
        filterName={FilterName.Status}
        options={statusOptions}
        handleFilter={handleFilter}
        handleHide={handleHide}
      />
      <TextInput 
        canHide={canHide}
        filterName={FilterName.Subject} 
        handleFilter={handleFilter} 
        handleHide={handleHide}
        minLength={5}
      />
      <SelectInput
        canHide={canHide}
        filterName={FilterName.Team}
        options={teams}
        handleFilter={handleFilter}
        handleHide={handleHide}
      />
      <ChooseFilters 
        toggleHide={() => {
          setCanHide(state => !state);
        }}
      />
      <FilterActionButtons />
      <SavedFilters checkBoxChecked={checkBoxChecked} setCheckBoxChecked={setCheckBoxChecked} />
    </>
  );
}

